import { type ChangeEvent, type FC, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { type Matcher, type Mode, type PropsBase } from 'react-day-picker';
import { Calendar as CalendarIcon } from 'lucide-react';
import { withMask } from 'use-mask-input';
import { format } from 'date-fns';
import { DateTimeGranularity, getDateTimeInputConfig, parseDateTimeInput } from '@/utilities/date';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type DatePickerProps = {
  value?: string | null | Date;
  onChange: (value: string | null) => void;
  className?: string;
  open?: boolean;
  setOpen?: (value: boolean) => void;
  disabled?: boolean;
  appendIcon?: React.ReactNode;
  startMonth?: Date;
  endMonth?: Date;
  disabledDates?: Matcher;
  indeterminate?: boolean;
  hideNavigation?: boolean;
  mode?: Mode;
  captionLayout?: PropsBase['captionLayout'];
  autoFocus?: boolean;
};

export const DatePicker: FC<DatePickerProps> = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      open,
      setOpen,
      disabled,
      startMonth,
      endMonth,
      disabledDates,
      hideNavigation,
      captionLayout = 'dropdown',
      className = '',
      autoFocus = false,
      ...props
    },
    ref,
  ) => {
    const tCommon = useTranslations('common');
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [month, setMonth] = useState(new Date());

    const [inputValue, setInputValue] = useState('');
    const [internalOpen, setInternalOpen] = useState(false);
    const inputMaskRef = useRef<HTMLInputElement | null>(null);
    const iconButtonRef = useRef<HTMLButtonElement | null>(null);
    const inputConfig = useMemo(() => getDateTimeInputConfig(DateTimeGranularity.Day), []);
    const maskRef = useMemo(
      () =>
        withMask(inputConfig.mask, {
          showMaskOnHover: false,
          showMaskOnFocus: true,
          clearMaskOnLostFocus: true,
        }),
      [inputConfig.mask],
    );

    const popoverOpen = open ?? internalOpen;
    const handleOpenChange = (nextOpen: boolean) => {
      if (nextOpen === popoverOpen) {
        return;
      }
      if (open === undefined) {
        setInternalOpen(nextOpen);
      }
      setOpen?.(nextOpen);
    };

    useEffect(() => {
      if (!inputMaskRef.current) {
        return;
      }

      maskRef(inputMaskRef.current);

      return () => {
        maskRef(null);
      };
    }, [maskRef]);

    const focusInput = () => {
      requestAnimationFrame(() => {
        inputMaskRef.current?.focus();
      });
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value;

      if (nextValue.length === 0) {
        setInputValue('');
        setSelectedDate(undefined);
        props.onChange(null);
        return;
      }

      if (nextValue.length < inputConfig.length) {
        setInputValue(nextValue);
        setSelectedDate(undefined);
        props.onChange(nextValue);
        return;
      }

      const parsedDate = parseDateTimeInput(nextValue, DateTimeGranularity.Day);
      if (!parsedDate) {
        setInputValue(nextValue);
        setSelectedDate(undefined);
        props.onChange(nextValue);
        return;
      }

      const formattedValue = format(parsedDate, inputConfig.format);
      setSelectedDate(parsedDate);
      setMonth(parsedDate);
      setInputValue(formattedValue);
      props.onChange(formattedValue);
    };

    const handleDayPickerSelect = (date: Date | undefined) => {
      if (!date) {
        setInputValue('');
        setSelectedDate(undefined);
      } else {
        setSelectedDate(date);
        setMonth(date);
        setInputValue(format(date, inputConfig.format));
        props.onChange(format(date, inputConfig.format));
      }
      handleOpenChange(false);
    };

    useEffect(() => {
      if (!value) {
        // If the user input an invalid date
        setSelectedDate(undefined);
        // If X button is clicked or value cleared
        setInputValue('');
      } else if (value instanceof Date) {
        setSelectedDate(value);
        setMonth(value);
        setInputValue(format(value, inputConfig.format));
      } else if (typeof value === 'string') {
        const parsedDate = parseDateTimeInput(value, DateTimeGranularity.Day);
        setSelectedDate(parsedDate);
        if (parsedDate) {
          setInputValue(format(parsedDate, inputConfig.format));
        } else {
          setInputValue(value);
        }
      }
    }, [value, inputConfig.format, inputConfig.length]);

    return (
      <Input
        disabled={disabled}
        autoFocus={autoFocus}
        type="text"
        ref={ref}
        className={cn('px-0', className)}
        safeInputRef={inputMaskRef}
        inputMode="numeric"
        maxLength={inputConfig.length}
        onClick={(e) => {
          (e.target as HTMLInputElement).focus();
          // inputmask re-dispatches a synthetic click on the input while handling key events
          // (its keyEvent handler calls trigger('click')), so a keystroke — Escape especially,
          // which also closes the surrounding dialog — would otherwise pop the calendar open
          // for an instant. That synthetic event is a CustomEvent whose detail is null, while a
          // real pointer click is a MouseEvent carrying a click count of at least 1. A text
          // input cannot be activated from the keyboard, so a falsy detail here always means
          // this was not a user click.
          if (!e.detail) {
            return;
          }
          if (!disabled && !popoverOpen) {
            handleOpenChange(true);
          }
        }}
        value={inputValue}
        placeholder={inputConfig.placeholder}
        prependIcon={
          <Popover modal={false} open={popoverOpen} onOpenChange={handleOpenChange}>
            <PopoverAnchor asChild>
              <Button
                ref={iconButtonRef}
                type="button"
                variant="ghost"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  handleOpenChange(true);
                  focusInput();
                }}
                className={cn(
                  'mx-0.5 min-h-full rounded-[7px] border-0 p-0.75 duration-150 hover:bg-transparent! focus-visible:bg-ui-base focus-visible:ring-0',
                  props.indeterminate ? 'text-status-warning!' : 'text-icon-base! hover:text-text-base!',
                )}
                aria-label={tCommon('openDateSelector')}
              >
                <CalendarIcon className="icon-4" strokeWidth={1.5} />
              </Button>
            </PopoverAnchor>
            <PopoverContent
              className="w-auto p-0"
              onCloseAutoFocus={(event) => {
                event.preventDefault();
              }}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
              }}
              onFocusOutside={(event) => {
                const target = event.target as Node;
                if (target === inputMaskRef.current || iconButtonRef.current?.contains(target)) {
                  event.preventDefault();
                }
              }}
              onInteractOutside={(event) => {
                const target = event.target as Node;
                if (target === inputMaskRef.current || iconButtonRef.current?.contains(target)) {
                  event.preventDefault();
                }
              }}
            >
              <Calendar
                captionLayout={captionLayout}
                hideNavigation={hideNavigation}
                startMonth={startMonth}
                endMonth={endMonth}
                month={month}
                onMonthChange={setMonth}
                mode="single"
                selected={selectedDate}
                onSelect={handleDayPickerSelect}
                defaultMonth={selectedDate ? new Date(selectedDate) : undefined}
                classNames={{ caption_label: 'hidden' }}
                disabled={disabledDates}
              />
            </PopoverContent>
          </Popover>
        }
        {...props}
        onChange={handleInputChange}
      />
    );
  },
);

DatePicker.displayName = 'DatePicker';
